import { Box, Flex, useDisclosure } from "@chakra-ui/react";
import {
  Table,
  TableContainer,
  Tbody,
  Th,
  Thead,
  Tr,
  Td,
  Badge,
  Button,
  Text,
  IconButton,
  MenuButton,
  Menu,
  MenuList,
  MenuItem,
  Checkbox,
} from "@chakra-ui/react";
import { CSVLink } from "react-csv";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useContext, useState } from "react";
import { apiRequest } from "utils/api";
import { LoanContext } from "providers/loan";
import { AlertsContext } from "providers/alerts";
import { generateBulletPaymentRows } from "utils/downloads";
import { LoanModel } from "providers/loans/loan.model";
import { useCustomFormats } from "hooks/useCustomFormat";
import { PaymentModal } from "./PaymentModal";
import { AccountReceivableModel } from "providers/loans/account-receivable.model";
import { useTenant } from "modules/tenant/providers/tenant-provider";

const BulletPayemtsTable = ({ loan }: { loan: LoanModel }) => {
  const { user } = useTenant();
  const { formatDate, formatCurrency } = useCustomFormats();
  const { isOpen, onClose, onOpen } = useDisclosure();
  const { showSuccess, showError } = useContext(AlertsContext);

  const { accountsReceivables, setAccountsReceivables } = useContext(LoanContext);

  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [selectAllChecked, setSelectAllChecked] = useState(false);

  const [payment, setPayment] = useState<AccountReceivableModel>();

  function handleOpenModal(payment?: AccountReceivableModel) {
    setPayment(payment);
    onOpen();
  }

  const handleRowSelect = (id: number) => {
    setSelectedRows(prev => {
      if (prev.includes(id)) {
        return prev.filter(rowId => rowId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const selectedPayments = accountsReceivables.filter((payment: { id: any }) =>
    selectedRows.includes(payment.id)
  );

  const DownloadLink = ({ selectedPayments }: { selectedPayments: any[] }) => {
    return selectedPayments.length > 0 ? (
      <CSVLink
        data={generateBulletPaymentRows(selectedPayments)}
        filename={`bullet_payments_export_${new Date().getTime()}.csv`}
        style={{
          borderWidth: "1px",
          borderColor: "primary",
          color: "primary",
          paddingLeft: "1rem",
          paddingRight: "1rem",
          marginRight: "12px",
          borderRadius: "7px",
          display: "flex",
          alignItems: "center",
          verticalAlign: "middle",
          justifyContent: "center",
        }}
      >
        <FontAwesomeIcon icon={["fas", "download"]} />
        <Box>
          <Text color={"black"} p={"5px"} ml={"5px"}>
            Export
          </Text>
        </Box>
      </CSVLink>
    ) : null;
  };

  const getBulletPayments = async () => {
    try {
      const { data } = await apiRequest("/supabase/accounts_receivable/select", { loan: loan!.id });
      setAccountsReceivables(data);
    } catch (error) {
      console.log(error);
    }
  };

  const handleSelectAllRows = () => {
    const allIds = accountsReceivables.map(obj => obj.id);
    const allVisibleRowsSelected = selectedRows.length === allIds.length;
    const updatedSelection = allVisibleRowsSelected ? [] : allIds;
    setSelectAllChecked(!allVisibleRowsSelected);
    setSelectedRows(updatedSelection);
  };

  const handleDelete = async (id: any) => {
    try {
      await apiRequest("/supabase/accounts_receivable/delete", {
        id: id,
      });
      showSuccess({
        title: "Payment Deleted",
        description: "The payment has been deleted",
      });
    } catch (error) {
      showError({
        title: "Something went wrong",
        description: "error",
      });
    } finally {
      getBulletPayments();
    }
  };

  return (
    <Flex direction="column" gap={4}>
      {user.type === "lender" && (
        <Flex gap={2} justifyContent="flex-end">
          <DownloadLink selectedPayments={selectedPayments} />
          <Button onClick={() => handleOpenModal()} variant="white" size="sm">
            Add Bullet Payment
          </Button>
        </Flex>
      )}

      <TableContainer>
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th w="36px">
                <Flex alignItems="center" justifyContent="center">
                  <Checkbox isChecked={selectAllChecked} onChange={handleSelectAllRows} />
                </Flex>
              </Th>
              <Th>Description</Th>
              <Th>Payment Date</Th>
              <Th>Payment Amount</Th>
              <Th>Payment Status</Th>
              {user.type === "lender" && <Th w="36px"></Th>}
            </Tr>
          </Thead>
          <Tbody>
            {accountsReceivables.length === 0 && (
              <Tr>
                <Td colSpan={5} textAlign="center">
                  <Text p={4}>No bullet payments yet.</Text>
                </Td>
              </Tr>
            )}
            {accountsReceivables.map(payment => (
              <Tr key={payment.id}>
                <Td>
                  <Flex alignItems="center">
                    <Checkbox
                      isChecked={selectedRows.includes(payment.id)}
                      onChange={() => handleRowSelect(payment.id)}
                    />
                  </Flex>
                </Td>
                <Td>{payment.description}</Td>
                <Td>{formatDate(payment?.payment_date)}</Td>
                <Td>{formatCurrency(payment?.payment_amount ?? 0)}</Td>
                <Td>
                  <Badge py={0} bg="#601DCB15" color="#601DCB">
                    {payment.payment_status}
                  </Badge>
                </Td>
                {user.type === "lender" && (
                  <Td>
                    <Menu>
                      <MenuButton
                        as={IconButton}
                        aria-label="Options"
                        variant={"link"}
                        icon={<FontAwesomeIcon icon={["far", "ellipsis-vertical"]} />}
                      />
                      <MenuList>
                        <MenuItem onClick={() => handleOpenModal(payment)}>Edit</MenuItem>
                        <MenuItem color={"red"} onClick={() => handleDelete(payment.id)}>
                          Delete
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  </Td>
                )}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>

      {isOpen && (
        <PaymentModal
          isOpen={isOpen}
          onClose={onClose}
          onSave={getBulletPayments}
          payment={payment}
        />
      )}
    </Flex>
  );
};

export default BulletPayemtsTable;
